// Pure game-logic helpers for BlastNova.
// Grid is an 8x8 array; each cell is either null (empty) or { color, glow }.

export const BOARD_SIZE = 8;

export function makeEmptyBoard() {
    return Array.from({ length: BOARD_SIZE }, () =>
        Array.from({ length: BOARD_SIZE }, () => null)
    );
}

/**
 * Check whether a shape can be placed with its top-left corner at (row, col).
 */
export function canPlace(board, cells, row, col) {
    const rows = cells.length;
    const cols = cells[0].length;
    if (row < 0 || col < 0 || row + rows > BOARD_SIZE || col + cols > BOARD_SIZE)
        return false;
    for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
            if (cells[r][c] === 1 && board[row + r][col + c] !== null) {
                return false;
            }
        }
    }
    return true;
}

/**
 * Return a NEW board with the shape placed. Does not mutate the input.
 */
export function placeShape(board, cells, row, col, theme) {
    const next = board.map((row) => row.slice());
    for (let r = 0; r < cells.length; r++) {
        for (let c = 0; c < cells[0].length; c++) {
            if (cells[r][c] === 1) {
                next[row + r][col + c] = {
                    color: theme.color,
                    glow: theme.glow,
                };
            }
        }
    }
    return next;
}

/**
 * Find indices of fully-filled rows and columns.
 */
export function findFullLines(board) {
    const fullRows = [];
    const fullCols = [];
    for (let r = 0; r < BOARD_SIZE; r++) {
        if (board[r].every((c) => c !== null)) fullRows.push(r);
    }
    for (let c = 0; c < BOARD_SIZE; c++) {
        let full = true;
        for (let r = 0; r < BOARD_SIZE; r++) {
            if (board[r][c] === null) {
                full = false;
                break;
            }
        }
        if (full) fullCols.push(c);
    }
    return { fullRows, fullCols };
}

/**
 * Get the set of [r, c] coordinates that should clear given full rows/cols.
 */
export function getClearCoords(fullRows, fullCols) {
    const coords = new Set();
    fullRows.forEach((r) => {
        for (let c = 0; c < BOARD_SIZE; c++) coords.add(`${r},${c}`);
    });
    fullCols.forEach((c) => {
        for (let r = 0; r < BOARD_SIZE; r++) coords.add(`${r},${c}`);
    });
    return coords;
}

/**
 * Apply clears to a board (return new board with cleared cells = null).
 */
export function applyClears(board, clearCoords) {
    return board.map((row, r) =>
        row.map((cell, c) => (clearCoords.has(`${r},${c}`) ? null : cell))
    );
}

/**
 * Can ANY of the provided pieces be placed somewhere on the board?
 * pieces: array of { cells } | null
 */
export function hasAnyValidMove(board, pieces) {
    for (const piece of pieces) {
        if (!piece) continue;
        const { cells } = piece;
        const rows = cells.length;
        const cols = cells[0].length;
        for (let r = 0; r <= BOARD_SIZE - rows; r++) {
            for (let c = 0; c <= BOARD_SIZE - cols; c++) {
                if (canPlace(board, cells, r, c)) return true;
            }
        }
    }
    return false;
}

/**
 * Can THIS single piece be placed anywhere on the board?
 */
export function pieceFitsAnywhere(board, cells) {
    const rows = cells.length;
    const cols = cells[0].length;
    for (let r = 0; r <= BOARD_SIZE - rows; r++) {
        for (let c = 0; c <= BOARD_SIZE - cols; c++) {
            if (canPlace(board, cells, r, c)) return true;
        }
    }
    return false;
}

/**
 * Compute score earned from a placement.
 *   - placement: number of cells in the piece
 *   - line clears: 10 points per cell cleared
 *   - combo: ×(1 + 0.5*(linesCleared-1)) for 2+ lines simultaneously
 */
export function computeScore({ piecePoints, linesCleared, cellsCleared, comboStreak }) {
    let score = piecePoints;
    if (linesCleared > 0) {
        const multi = 1 + 0.5 * (linesCleared - 1);
        score += Math.round(cellsCleared * 10 * multi);
    }
    if (comboStreak >= 2) {
        // back-to-back placements that each clear lines
        score += 20 * comboStreak;
    }
    return score;
}
